import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Shield, Mail, Users,
  RefreshCw, LogOut, AlertCircle, GraduationCap, Smartphone, Zap, Activity,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import Brand from "../components/Brand";
import RosterTab from "./admin/RosterTab";
import EmailsTab from "./admin/EmailsTab";
import DevicesTab from "./admin/DevicesTab";
import ContentTab from "./admin/ContentTab";
import TeachersTab from "./admin/TeachersTab";
import OverviewTab from "./admin/OverviewTab";

/** Lean /admin page shell: auth gate + header + tab switcher. All tab content
 *  lives in `pages/admin/*Tab.jsx`. */
export default function Admin() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  const handleGoogleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth-callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (loading) {
    return <div className="min-h-screen bg-bone flex items-center justify-center text-sage">Loading…</div>;
  }

  if (!user || user.role !== "admin") {
    const notAuthorized = user && user.role !== "admin";
    const adminEmail = "t.m@mimc.online";
    return (
      <div className="min-h-screen bg-bone flex flex-col items-center justify-center px-4">
        <div className="mb-6">
          <Brand size="md" linkTo="/" />
        </div>
        <div className="w-full max-w-md bg-white border border-border rounded-3xl p-8 sm:p-10 shadow-sm">
          <div className="w-12 h-12 rounded-2xl bg-sage flex items-center justify-center text-white mb-5">
            <Shield className="w-6 h-6" strokeWidth={1.7} />
          </div>
          <h1 className="font-display text-2xl font-semibold tracking-tight mb-2">Admin access</h1>
          <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
            Only the <span className="font-medium text-foreground">{adminEmail}</span> Google account can access the admin console.
          </p>

          {notAuthorized && (
            <div className="mb-5 p-3 rounded-lg bg-terracotta/10 border border-terracotta/30 text-sm text-terracotta flex items-start gap-2" data-testid="admin-login-error">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                You're signed in as <span className="font-semibold">{user.email}</span>, which isn't the admin account.
                Sign out and sign back in with <span className="font-semibold">{adminEmail}</span>.
              </span>
            </div>
          )}

          <button
            onClick={handleGoogleSignIn}
            data-testid="admin-google-signin"
            className="w-full inline-flex items-center justify-center gap-3 bg-sage hover:bg-sage-dark text-white font-medium px-5 py-3 rounded-xl transition-all"
          >
            <GoogleIcon />
            {notAuthorized ? "Sign in with a different Google account" : "Sign in with Google"}
          </button>

          {notAuthorized ? (
            <button
              onClick={async () => {
                await logout();
                window.location.href = "https://accounts.google.com/Logout?continue=" +
                  encodeURIComponent(window.location.origin + "/admin");
              }}
              data-testid="admin-full-signout"
              className="w-full mt-3 text-xs text-sage hover:underline py-2"
            >
              Sign out of Google & try again
            </button>
          ) : (
            <div className="mt-4 p-3 rounded-lg bg-secondary/60 text-[11px] text-muted-foreground leading-relaxed">
              <span className="font-medium text-foreground">Tip:</span> if your browser signs you into the wrong Google account,
              <a href={`https://accounts.google.com/Logout?continue=${encodeURIComponent(window.location.origin + "/admin")}`}
                 className="text-sage underline ms-1">sign out of Google first</a>
              , then click the button above.
            </div>
          )}

          <button onClick={() => navigate("/")} className="mt-5 text-xs text-muted-foreground hover:text-sage block mx-auto">← Back to home</button>
        </div>
      </div>
    );
  }

  return <AdminDashboard onLogout={logout} />;
}

const TABS = [
  { key: "overview", label: "Overview", icon: Activity },
  { key: "emails", label: "Allowed emails", icon: Mail },
  { key: "teachers", label: "Teachers", icon: GraduationCap },
  { key: "roster", label: "Students", icon: Users },
  { key: "devices", label: "Devices", icon: Smartphone },
  { key: "content", label: "Content", icon: Zap },
];

function AdminDashboard({ onLogout }) {
  const [tab, setTab] = useState("overview");

  return (
    <div className="min-h-screen bg-bone">
      <header className="sticky top-0 z-40 border-b border-border bg-bone/80 backdrop-blur">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <Brand size="sm" linkTo={null} />
            <div className="hidden sm:flex items-center gap-2 ps-3 border-s border-border">
              <div className="w-6 h-6 rounded-md bg-sage/10 flex items-center justify-center text-sage">
                <Shield className="w-3.5 h-3.5" strokeWidth={2} />
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Admin Console</div>
            </div>
          </div>
          <button
            onClick={onLogout}
            title="Sign out"
            aria-label="Sign out"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-sage p-2 sm:px-3 sm:py-1.5 rounded-lg hover:bg-white"
            data-testid="admin-logout"
          >
            <LogOut className="w-4 h-4 shrink-0" />
            <span className="hidden sm:inline whitespace-nowrap">Sign out</span>
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="mb-6 sm:mb-8">
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">Admin Console</h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1.5">
            Add allowed emails for students or teachers — they sign in with Google straight away. Control the device limit per account here too.
          </p>
        </div>

        <div className="flex flex-wrap gap-1 mb-6 sm:mb-8 p-1.5 bg-white border border-border rounded-2xl w-full sm:w-fit overflow-x-auto">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`admin-tab-${t.key}`}
                className={`inline-flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${
                  active ? "bg-sage text-white" : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                }`}
              >
                <Icon className="w-4 h-4 shrink-0" /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "overview" && <OverviewTab />}
        {tab === "emails" && <EmailsTab />}
        {tab === "teachers" && <TeachersTab />}
        {tab === "roster" && <RosterTab />}
        {tab === "devices" && <DevicesTab />}
        {tab === "content" && <ContentTab />}
      </div>

      {/* Visual refresh icon — kept for any consumer that looks for it */}
      <RefreshCw className="hidden" />
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path fill="#fff" d="M21.805 10.023h-9.765v3.955h5.627c-.544 2.56-2.603 3.945-5.627 3.945a6.273 6.273 0 01-6.273-6.273 6.273 6.273 0 016.273-6.273c1.49 0 2.834.527 3.894 1.395l2.977-2.977C16.953 2.148 14.591 1 12.04 1A11 11 0 001.04 12a11 11 0 0011 11c5.5 0 10.25-4 10.25-11 0-.637-.066-1.277-.195-1.977z"/>
    </svg>
  );
}
